import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import DataToolbar, { StatusPill, Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { Button } from '../../components/ui/button';
import { Plus, Home, MoreHorizontal, Edit2, Trash2, Eye, Star, Images } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { useApi } from '../../api/useApi';
import { listProperties, deleteProperty, updateProperty } from '../../api/hostApi';
import { toast } from 'sonner';

const imageUrl = (property) => property?.cover_image?.url || property?.cover_image || property?.coverImage || 'https://placehold.co/600x360';

export default function HostProperties() {
  const { data, loading, error, refetch } = useApi(listProperties, []);
  const properties = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');

  const filtered = properties.filter((p) => {
    const matchQ = !q || `${p.title} ${p.location_details?.city || p.city || ''} ${p.location_details?.state || p.state || ''}`.toLowerCase().includes(q.toLowerCase());
    const matchS = !status || (p.status || '').toLowerCase() === status.toLowerCase();
    return matchQ && matchS;
  });

  const togglePublish = async (p) => {
    const next = p.status === 'published' ? 'draft' : 'published';
    try {
      await updateProperty(p.id, { status: next });
      toast.success(next === 'published' ? 'Property published' : 'Property moved to draft');
      refetch();
    } catch (e) {
      toast.error(e?.message || 'Update failed');
    }
  };

  const del = async (p) => {
    try {
      await deleteProperty(p.id);
      toast.success('Property archived');
      refetch();
    } catch (e) {
      toast.error(e?.message || 'Archive failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Properties"
        description="Create, edit and manage your property listings."
        actions={
          <Button asChild className="bg-teal-700 hover:bg-teal-800 h-10">
            <Link to="/host/properties/new"><Plus className="h-4 w-4 mr-1" /> New property</Link>
          </Button>
        }
      />
      <DataToolbar
        search={q}
        onSearch={setQ}
        placeholder="Search title, city, state…"
        filters={[{ label: 'Status', value: status, options: ['All', 'Published', 'Draft', 'Archived'], onChange: setStatus }]}
      />

      {loading ? <LoadingState label="Loading properties…" />
        : error ? <ErrorState error={error} onRetry={refetch} />
        : filtered.length === 0 ? (
          <EmptyResults icon={Home} title="No properties yet" description="Create your first listing to start receiving bookings." />
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((p) => {
              const city = p.location_details?.city || p.city || '';
              const state = p.location_details?.state || p.state || '';
              return (
                <Card key={p.id} className="overflow-hidden card-hover">
                  <Link to={`/host/properties/${p.id}`} className="block aspect-[16/10] overflow-hidden relative">
                    <img src={imageUrl(p)} alt="" className="h-full w-full object-cover" />
                    <div className="absolute top-3 left-3"><StatusPill status={p.status} /></div>
                  </Link>
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <Link to={`/host/properties/${p.id}`}><h3 className="font-semibold text-slate-900 truncate hover:text-teal-700">{p.title}</h3></Link>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{p.location || `${city}${state ? ', ' + state : ''}`}</p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="h-8 w-8 -mr-2 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100"><MoreHorizontal className="h-4 w-4" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild><Link to={`/host/properties/${p.id}`}><Eye className="h-4 w-4 mr-2" /> Manage</Link></DropdownMenuItem>
                          <DropdownMenuItem asChild><Link to={`/host/properties/${p.id}/edit`}><Edit2 className="h-4 w-4 mr-2" /> Edit basics</Link></DropdownMenuItem>
                          <DropdownMenuItem asChild><Link to={`/host/properties/${p.id}/images`}><Images className="h-4 w-4 mr-2" /> Manage images</Link></DropdownMenuItem>
                          <DropdownMenuItem onClick={() => togglePublish(p)}>{p.status === 'published' ? 'Move to draft' : 'Publish'}</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-rose-600 focus:text-rose-700"><Trash2 className="h-4 w-4 mr-2" /> Archive</DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Archive this property?</AlertDialogTitle>
                                <AlertDialogDescription>“{p.title}” will be removed from active listings but retained in StayEasy.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => del(p)}>Archive</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-sm"><span className="font-bold text-slate-900 tabular-nums">${p.price_per_night ?? p.pricePerNight ?? 0}</span><span className="text-slate-500 text-xs"> /night</span></div>
                      <div className="flex items-center gap-1 text-xs text-slate-500"><Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" /> {p.average_rating ?? p.rating ?? '—'} <span className="text-slate-400">({p.review_count ?? p.reviews_count ?? p.reviewsCount ?? 0})</span></div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="rounded-lg bg-slate-50 py-1.5"><div className="font-semibold text-slate-900">{p.beds ?? p.bedrooms ?? '—'}</div><div className="text-slate-500">Beds</div></div>
                      <div className="rounded-lg bg-slate-50 py-1.5"><div className="font-semibold text-slate-900">{p.baths ?? p.bathrooms ?? '—'}</div><div className="text-slate-500">Baths</div></div>
                      <div className="rounded-lg bg-slate-50 py-1.5"><div className="font-semibold text-slate-900">{p.max_guests ?? p.maxGuests ?? '—'}</div><div className="text-slate-500">Guests</div></div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )
      }
    </div>
  );
}
